import { useTranslateAssetType } from 'hooks/use-translate-payoff';
import type { ProductVariation } from 'models/productsReferential';
import { useMemo } from 'react';
import { optionalMap } from 'utils/optional';

const assetTypesWantedOrder = ['Equity', 'Credit', 'Rate', 'Forex', 'Pinned'];

const getBootstrapIconNameForAssetType = (assetType: string): string => {
  switch (assetType) {
    case 'Equity':
      return 'balance';
    case 'Rate':
      return 'percent';
    case 'Credit':
      return 'toll';
    case 'Forex':
      return 'currency_exchange';
    case 'Pinned':
      return 'star_outline';
    default:
      return 'box';
  }
};

export type AssetTypeOption = {
  label?: string;
  value: string;
  icon: string;
};

export const useGetAssetTypesOptions = (productVariations: readonly ProductVariation[]): AssetTypeOption[] => {
  const toLabel = optionalMap(useTranslateAssetType());

  return useMemo(() => {
    const distinctAssetTypeOptions = productVariations
      .flatMap(({ assetTypes }) => assetTypes ?? [])
      .filter((assetType, pos, all) => all.indexOf(assetType) === pos) // avoid duplication of asset types by only taking the first occurrence
      .map(assetType => ({
        label: toLabel(assetType),
        value: assetType,
        icon: getBootstrapIconNameForAssetType(assetType),
      }))
      .sort((a, b) => assetTypesWantedOrder.indexOf(a.value) - assetTypesWantedOrder.indexOf(b.value));

    // if no pinned products then add the option to the list to display it at least
    if (!distinctAssetTypeOptions.some(assetType => assetType.value === 'Pinned')) {
      return [
        ...distinctAssetTypeOptions,
        {
          label: toLabel('Pinned'),
          value: 'Pinned',
          icon: getBootstrapIconNameForAssetType('Pinned'),
        },
      ];
    }

    return distinctAssetTypeOptions;
  }, [productVariations, toLabel]);
};
